import { useState } from 'react';
import { BusinesscaseExtraCost } from '@/types/businesscase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ExtraCostsManagerProps {
  businesscaseId: string;
  extraCosts: BusinesscaseExtraCost[];
  onUpdate: () => void;
}

export function ExtraCostsManager({ businesscaseId, extraCosts, onUpdate }: ExtraCostsManagerProps) {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newCostName, setNewCostName] = useState('');
  const [newCostAmount, setNewCostAmount] = useState(0);

  const handleAdd = async () => {
    if (!newCostName.trim()) {
      toast({
        title: 'Naam vereist',
        description: 'Geef een naam op voor de extra kosten',
        variant: 'destructive'
      });
      return;
    }

    const { error } = await supabase
      .from('businesscase_extra_costs')
      .insert({
        businesscase_id: businesscaseId,
        name: newCostName.trim(),
        amount: newCostAmount
      });

    if (error) {
      toast({
        title: 'Fout bij toevoegen',
        description: error.message,
        variant: 'destructive'
      });
      return;
    }

    toast({
      title: 'Extra kosten toegevoegd',
      description: `${newCostName} toegevoegd aan CAPEX`
    });

    setNewCostName('');
    setNewCostAmount(0);
    setIsAdding(false);
    onUpdate();
  };

  const handleUpdate = async (id: string, field: 'name' | 'amount', value: string | number) => {
    const { error } = await supabase
      .from('businesscase_extra_costs')
      .update({ [field]: value })
      .eq('id', id);

    if (error) {
      toast({
        title: 'Fout bij opslaan',
        description: error.message,
        variant: 'destructive'
      });
      return;
    }

    onUpdate();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('businesscase_extra_costs')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive'
      });
      return;
    }

    toast({
      title: 'Extra kosten verwijderd',
      description: 'Kosten verwijderd uit CAPEX'
    });

    onUpdate();
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Extra Kosten
          <Button onClick={() => setIsAdding(!isAdding)} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Toevoegen
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdding && (
          <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/50">
            <div className="space-y-2">
              <Label htmlFor="new-cost-name">Naam</Label>
              <Input
                id="new-cost-name"
                value={newCostName}
                onChange={(e) => setNewCostName(e.target.value)}
                placeholder="bijv. Transportkosten"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-cost-amount">Bedrag (€)</Label>
              <Input
                id="new-cost-amount"
                type="number"
                value={newCostAmount}
                onChange={(e) => setNewCostAmount(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdd} size="sm">Opslaan</Button>
              <Button onClick={() => setIsAdding(false)} size="sm" variant="outline">Annuleren</Button>
            </div>
          </div>
        )}

        {extraCosts.map((cost) => (
          <div key={cost.id} className="border border-border rounded-lg p-4 space-y-3">
            <div className="space-y-2">
              <Label>Naam</Label>
              <Input
                value={cost.name}
                onChange={(e) => handleUpdate(cost.id, 'name', e.target.value)}
                onBlur={(e) => handleUpdate(cost.id, 'name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Bedrag (€)</Label>
              <Input
                type="number"
                value={cost.amount}
                onChange={(e) => handleUpdate(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                onBlur={(e) => handleUpdate(cost.id, 'amount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <Button 
              onClick={() => handleDelete(cost.id)} 
              size="sm" 
              variant="destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Verwijderen
            </Button>
          </div>
        ))}

        {extraCosts.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Geen extra kosten toegevoegd
          </p>
        )}
      </CardContent>
    </Card>
  );
}
